import { ChangeDetectionStrategy, Component } from '@angular/core';

import { ButtonComponent, IconComponent, TableComponent } from '@core/user-interface-legacy';

interface AppEnvironment {
  name: string;
  production: string;
  rc: string;
  staging: string;
}

const APPS: AppEnvironment[] = [
  {
    name: 'Recruitee',
    production: 'https://app.recruitee.com',
    rc: 'https://app.rc.recruitee.dev',
    staging: 'https://app.s.recruitee.com',
  },
  {
    name: 'Tellent Admin',
    production: 'https://admin.tellent.com',
    rc: 'https://admin.rc.tellent.com',
    staging: 'https://admin.s.tellent.com',
  },
  {
    name: 'Onboarding',
    production: 'https://setup.recruitee.com',
    rc: 'https://setup.rc.recruitee.dev',
    staging: 'https://setup.s.recruitee.com',
  },
  {
    name: 'Viewbox',
    production: 'https://company.recruitee.com/v/',
    rc: 'https://company.rc.recruitee.dev/v/',
    staging: 'https://company.s.recruitee.com/v/',
  },
  {
    name: 'Referrals',
    production: 'https://recruitee.referrals.co',
    rc: 'https://referrals.rc.recruitee.dev',
    staging: 'https://moropol.s.referrals.co',
  },
  {
    name: 'Recruitee Mobile',
    production: 'https://mobile.recruitee.com',
    rc: 'https://mobile.rc.recruitee.dev',
    staging: 'https://mobile.s.recruitee.com',
  },
  {
    name: 'Integrations UI',
    production: 'https://integrations.recruitee.com',
    rc: 'https://integrations.rc.recruitee.dev',
    staging: 'https://integrations.s.recruitee.com',
  },
  {
    name: 'Product Updates',
    production: 'https://updates.tellent.com',
    rc: 'https://updates.rc.tellent.com',
    staging: 'https://updates.s.tellent.com',
  },
  {
    name: 'Prosemirror Converter',
    production: 'https://europe-west3-recruitee-1117.cloudfunctions.net/prosemirror-v2/version',
    rc: 'https://europe-west3-recruitee-release-candidate.cloudfunctions.net/prose-mirror/version',
    staging:
      'https://europe-west3-recruitee-staging.cloudfunctions.net/prose-mirror-converter/version',
  },
];

@Component({
  selector: 'app-environments',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableComponent, ButtonComponent, IconComponent],
  template: `
    <h2>Environments</h2>
    <rt-table variant="outline">
      <thead>
        <tr>
          <th>App</th>
          <th class="env-header">Staging</th>
          <th class="env-header">RC</th>
          <th class="env-header">Production</th>
        </tr>
      </thead>
      <tbody>
        @for (app of apps; track app.name) {
          <tr>
            <td class="app-name">{{ app.name }}</td>
            <td>
              <div class="env-cell">
                <a
                  rt-button
                  variant="outline-informative"
                  size="small"
                  [href]="app.staging"
                  target="_blank"
                  rel="noopener"
                >
                  <span class="env-text">{{ shortenUrl(app.staging) }}</span>
                  <rt-icon name="open-in-new" size="small-xx" suffix />
                </a>
              </div>
            </td>
            <td>
              <div class="env-cell">
                <a
                  rt-button
                  variant="outline-warning"
                  size="small"
                  [href]="app.rc"
                  target="_blank"
                  rel="noopener"
                >
                  <span class="env-text">{{ shortenUrl(app.rc) }}</span>
                  <rt-icon name="open-in-new" size="small-xx" suffix />
                </a>
              </div>
            </td>
            <td>
              <div class="env-cell">
                <a
                  rt-button
                  variant="outline-success"
                  size="small"
                  [href]="app.production"
                  target="_blank"
                  rel="noopener"
                >
                  <span class="env-text">{{ shortenUrl(app.production) }}</span>
                  <rt-icon name="open-in-new" size="small-xx" suffix />
                </a>
              </div>
            </td>
          </tr>
        }
      </tbody>
    </rt-table>
  `,
  styleUrl: './environments.component.less',
})
export class EnvironmentsComponent {
  protected readonly apps = APPS;

  protected shortenUrl(url: string): string {
    try {
      const { hostname } = new URL(url);
      return hostname;
    } catch {
      return url;
    }
  }
}
